import { buildKeypath, parseKeypath } from '@/data/idGenerator';
import { SCORECARD_MAX_POINTS } from '@/data/scorecardConfig';
import type {
  DataContext,
  FieldDefinition,
  Report,
  TemplateSection,
  TemplateViewBlock,
  ViewBlock,
  ViewSection,
} from '@/data/schemas';
import { VariableResolver } from '@/data/variableResolver';
import { buildTableViewBlock } from '../tableBlockHelpers';
import {
  resolveFieldEditingState,
  resolveRowEditingState,
  resolveSectionEditingState,
} from './editingState';
import { createFieldDisplayItem } from './fieldBuilder';

export function expandRepeatingSection(
  section: TemplateSection,
  report: Report,
  resolver: VariableResolver | null,
  locale: string,
  context: DataContext | null
): Array<{ viewSection: ViewSection; indices: number[] }> {
  if (section.type !== 'repeating' || !section.repeat) {
    return [];
  }

  const sectionKeyPattern = new RegExp(`^${section.id}\\[(\\d+)\\]\\.`);
  const indices = new Set<number>();

  for (const keypath of Object.keys(report.data.values)) {
    const match = keypath.match(sectionKeyPattern);
    if (match) {
      indices.add(parseInt(match[1], 10));
    }
  }

  if (indices.size === 0) {
    return [];
  }

  const sortedIndices = Array.from(indices).sort((a, b) => a - b);
  return sortedIndices.map((index) => ({
    viewSection: createViewSection(section, report, resolver, locale, context, [index]),
    indices: [index],
  }));
}

export function createViewSection(
  section: TemplateSection,
  report: Report,
  resolver: VariableResolver | null,
  locale: string,
  context: DataContext | null,
  indices?: number[]
): ViewSection {
  const blocks: ViewBlock[] = [];
  const highlights = report.data.highlights ?? {};
  const sectionRows = section.rows ?? [];
  const rowFieldIds = new Set<string>();
  const resolvedSectionEditing = resolveSectionEditingState(section.editing);
  let hasEditableOverrides = false;

  const rowBlocks = sectionRows
    .map((row) => {
      const resolvedRowEditing = resolveRowEditingState(section.editing, row.editing);
      const items = row.fieldIds
        .map((fieldId) => section.fields.find((field) => field.id === fieldId))
        .filter((field): field is FieldDefinition => Boolean(field))
        .map((field) => {
          rowFieldIds.add(field.id);
          const resolvedFieldEditing = resolveFieldEditingState({
            section: section.editing,
            row: row.editing,
            field: field.editing,
            hasSource: Boolean(field.source),
          });

          if (
            field.editing?.state === 'editable' ||
            (resolvedSectionEditing.state !== 'editable' &&
              resolvedFieldEditing.state === 'editable')
          ) {
            hasEditableOverrides = true;
          }

          return createFieldDisplayItem({
            field,
            sectionId: section.id,
            report,
            resolver,
            locale,
            highlights,
            indices,
            editingState: resolvedFieldEditing.state,
            editingReason: resolvedFieldEditing.reason,
            sectionDisableHighlight: section.disableHighlight ?? false,
          });
        });

      if (items.length === 0) {
        return null;
      }

      const layout = section.id === 'sec_societa' ? { columns: 1 } : row.layout;

      return {
        id: row.id,
        label: row.label,
        layout,
        editingState: resolvedRowEditing.state,
        editingReason: resolvedRowEditing.reason,
        items,
      };
    })
    .filter((row): row is NonNullable<typeof row> => row !== null);

  const defaultBlocks: ViewBlock[] = [];

  if (rowBlocks.length > 0) {
    defaultBlocks.push({
      type: 'row-group',
      rows: rowBlocks,
    });
  }

  const remainingFields = section.fields.filter((field) => !rowFieldIds.has(field.id));

  if (remainingFields.length > 0) {
    const items = remainingFields.map((field) => {
      const resolvedFieldEditing = resolveFieldEditingState({
        section: section.editing,
        field: field.editing,
        hasSource: Boolean(field.source),
      });

      if (
        field.editing?.state === 'editable' ||
        (resolvedSectionEditing.state !== 'editable' && resolvedFieldEditing.state === 'editable')
      ) {
        hasEditableOverrides = true;
      }

      return createFieldDisplayItem({
        field,
        sectionId: section.id,
        report,
        resolver,
        locale,
        highlights,
        indices,
        editingState: resolvedFieldEditing.state,
        editingReason: resolvedFieldEditing.reason,
        sectionDisableHighlight: section.disableHighlight ?? false,
      });
    });

    defaultBlocks.push({
      type: 'kv',
      items,
    });
  }

  const resolveImageSource = (source?: string): string | undefined => {
    if (!source || !resolver) {
      return undefined;
    }
    try {
      const resolved = resolver.resolveSource(source, 'text');
      if (resolved === undefined || resolved === null) {
        return undefined;
      }
      const text = String(resolved).trim();
      return text.length > 0 ? text : undefined;
    } catch {
      return undefined;
    }
  };

  const buildViewBlocksFromTemplate = (viewBlock: TemplateViewBlock): ViewBlock[] => {
    switch (viewBlock.type) {
      case 'table': {
        const tableBlock = buildTableViewBlock({
          block: viewBlock,
          report,
          sectionId: section.id,
          indices,
          context: context ?? undefined,
        });
        return [tableBlock];
      }
      case 'image': {
        return [
          {
            type: 'image',
            src: resolveImageSource(viewBlock.source),
            alt: viewBlock.alt,
            maxWidthCm: viewBlock.maxWidthCm,
            maxHeightCm: viewBlock.maxHeightCm,
            align: viewBlock.align,
            fallbackText: viewBlock.fallbackText,
          },
        ];
      }
      case 'scorecard-chart': {
        const seriesFieldIds = new Set(viewBlock.series.map((series) => series.fieldId));
        const entries = new Map<
          number,
          {
            index: number;
            date?: string;
            values: Record<string, number | null>;
          }
        >();

        const coerceScoreValue = (value: unknown): number | null => {
          if (value === null || value === undefined || value === '') return null;
          if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null;
          }
          const parsed = Number(value);
          return Number.isFinite(parsed) ? parsed : null;
        };

        Object.entries(report.data.values).forEach(([keypath, value]) => {
          let parsed;
          try {
            parsed = parseKeypath(keypath);
          } catch {
            return;
          }

          if (parsed.sectionId !== section.id) return;
          const entryIndex = parsed.indices[0];
          if (entryIndex === undefined) return;

          if (parsed.fieldId !== viewBlock.dateFieldId && !seriesFieldIds.has(parsed.fieldId)) {
            return;
          }

          const entry = entries.get(entryIndex) ?? {
            index: entryIndex,
            date: report.meta.visitDate,
            values: {},
          };

          if (parsed.fieldId === viewBlock.dateFieldId) {
            if (typeof value === 'string' && value.trim().length > 0) {
              entry.date = value;
            }
          } else {
            entry.values[parsed.fieldId] = coerceScoreValue(value);
          }

          entries.set(entryIndex, entry);
        });

        if (entries.size === 0 && report.meta.visitDate) {
          entries.set(0, {
            index: 0,
            date: report.meta.visitDate,
            values: {},
          });
        }

        const maxPoints = viewBlock.maxPoints ?? SCORECARD_MAX_POINTS;
        const orderedEntries = Array.from(entries.values())
          .sort((a, b) => a.index - b.index)
          .slice(-maxPoints);

        const noteKeypath = buildKeypath(section.id, viewBlock.noteFieldId, indices);
        const noteValue = report.data.values[noteKeypath];

        return [
          {
            type: 'scorecard-chart',
            title: viewBlock.title,
            series: viewBlock.series.map((series) => ({
              id: series.id,
              label: series.label,
              fieldId: series.fieldId,
              color: series.color,
            })),
            entries: orderedEntries.map((entry) => ({
              index: entry.index,
              date: entry.date,
              values: entry.values,
            })),
            noteFieldId: viewBlock.noteFieldId,
            noteValue: typeof noteValue === 'string' ? noteValue : undefined,
          },
        ];
      }
      case 'grid': {
        const columns = viewBlock.columns.map((column) => ({
          width: column.width,
          blocks: (column.blocks as TemplateViewBlock[]).flatMap((child) =>
            buildViewBlocksFromTemplate(child)
          ),
        }));

        return [
          {
            type: 'grid',
            columns,
          },
        ];
      }
      case 'section-content':
        return defaultBlocks;
      default:
        return [];
    }
  };

  if (section.viewBlocks?.length) {
    const mappedBlocks = section.viewBlocks.flatMap((viewBlock) =>
      buildViewBlocksFromTemplate(viewBlock as TemplateViewBlock)
    );

    if (section.viewBlocksMode === 'replace') {
      blocks.push(...mappedBlocks);
    } else {
      blocks.push(...defaultBlocks, ...mappedBlocks);
    }
  } else {
    blocks.push(...defaultBlocks);
  }

  let title = section.name;
  if (indices && indices.length > 0 && title.includes('{{')) {
    title = title.replace(/\{\{(\w+)\.(\w+)\}\}/g, (match, _prefix, fieldName) => {
      const field = section.fields.find((f) => {
        if (!f.source) return false;
        const sourceMatch = f.source.match(/\{\{(\w+)\.(\w+)\}\}/);
        if (!sourceMatch) return false;
        return sourceMatch[2] === fieldName;
      });

      if (field) {
        const fieldKeypath = buildKeypath(section.id, field.id, indices);
        const value = report.data.values[fieldKeypath];
        if (value !== undefined && value !== null && value !== '') {
          return String(value);
        }
      }
      return match;
    });
  }

  return {
    id: indices ? `${section.id}[${indices.join(',')}]` : section.id,
    title,
    blocks,
    editing: {
      state: resolvedSectionEditing.state,
      reason: resolvedSectionEditing.reason,
      hasEditableOverrides: hasEditableOverrides || undefined,
    },
  };
}
